import {
    GetEmployeeIssuesParams,
    GetRevenueIssuesParams,
    IgnoreIssuesParams,
    type Issue,
    IssueType,
    formatDate,
    getIssueMessage,
    getRange,
    monthNames,
    parseDateString,
    toDateString,
} from "@pentacode/core";
import "@pentacode/ui/components";
import { LitElement, css, html } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert, confirm } from "../../elements/alert-dialog";
import { Checkbox } from "../../elements/checkbox";
import "../../elements/issue";
import { app } from "../../init";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { mixins, shared } from "../../styles";
import { hasPageAccess } from "../app-routes";
import { routeAccess } from "../route-access";

interface IssueMonth {
    year: number;
    month: number;
    days: IssueDay[];
}

interface IssueDay {
    date: string;
    issues: Issue[];
}

const weekDays = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

@customElement("ptc-issues")
export class Issues extends Routing(StateMixin(LitElement)) {
    routePattern = /^issues/;

    get routeTitle() {
        return "Problembericht";
    }

    get helpPage() {
        return "/handbuch/probleme";
    }

    @query(".main")
    private _main: HTMLDivElement;

    @query("#filterForm")
    private _filterForm: HTMLFormElement;

    @state()
    private _currMonth: string;

    @state()
    private _issues: Issue[] = [];

    @state()
    private _loading = false;

    private get _filteredTypes() {
        return this._filterForm && new FormData(this._filterForm).getAll("types").map((t: string) => Number(t));
    }

    handleRoute() {
        void this._load();

        setTimeout(() => this._scroll(), 500);
    }

    updated() {
        this._scroll();
    }

    private async _load() {
        this._loading = true;

        try {
            // TODO: no DateRange restriction?
            const [dailyIssues, revenueIssues] = await Promise.all([
                app.api.getEmployeeIssues(new GetEmployeeIssuesParams({ includeIgnored: false })),
                app.api.getRevenueIssues(new GetRevenueIssuesParams({})),
            ]);
            this._issues = [...dailyIssues, ...revenueIssues].sort((a, b) => (a.date > b.date ? -1 : 1));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private _scroll() {
        if (!this._main) {
            return;
        }
        const { top, left, width } = this._main.getBoundingClientRect();
        const month = this.shadowRoot!.elementsFromPoint(left + width / 2, top + 30).find(
            (el: HTMLElement) => !!el.dataset.month
        );
        this._currMonth = (month && (month as HTMLElement).dataset.month) || "";
    }

    private _scrollToMonth(m: IssueMonth) {
        const month = this.renderRoot.querySelector(`[data-month="${m.year}-${m.month}"]`) as HTMLElement;
        month && month.scrollIntoView();
    }

    private async _ignoreIssues(issues: Issue[]) {
        const confirmed = await confirm(
            issues.length > 1
                ? "Wollen Sie diese Probleme wirklich verwerfen?"
                : "Wollen Sie dieses Problem wirklich verwerfen?",
            issues.length > 1 ? `${issues.length} Probleme Vewerfen` : "Problem Verwerfen",
            "Abbrechen",
            {
                title: issues.length > 1 ? "Probleme Verwerfen" : "Problem Verwerfen",
                icon: "icon.toggle.visibility-hidden",
            }
        );

        if (confirmed) {
            await app.api.ignoreIssues(new IgnoreIssuesParams({ issues }));
            await this._load();
        }
    }

    private _canResolve(issue: Issue) {
        const revenueIssue =
            issue.type === IssueType.UnbalancedDailyCash || issue.type === IssueType.PendingDailyRevenues;
        return !revenueIssue || hasPageAccess("revenues", routeAccess());
    }

    private _goToIssue(issue: Issue) {
        switch (issue.type) {
            case IssueType.UnbalancedDailyCash:
            case IssueType.PendingDailyRevenues:
                this.go(`revenues/daily`, { venue: issue.details.venueId, date: issue.date });
                break;
            default: {
                const now = toDateString(new Date());
                if (issue.date > now) {
                    this.go("roster", getRange(issue.date, "week"));
                } else {
                    this.go(`employees/${issue.details.employeeId}/time`, getRange(issue.date, "month"));
                }
            }
        }
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            :host {
                position: relative;
                display: flex;
            }

            .sub-menu {
                width: 15em;
                ${mixins.scroll()};
            }

            .issue-count {
                border-radius: calc(2 * var(--border-radius));
                color: var(--red);
                margin-bottom: 0.5em;
                border: solid 1px;
                font-size: var(--font-size-small);
                --color-highlight: var(--red);
                --color-highlight-bg: var(--red-bg);
            }

            .issue-count button {
                padding: 0.5em;
            }

            .main {
                ${mixins.scroll()};
                flex: 1;
                max-height: 100vh;
                max-width: 85em;
            }

            .month {
                position: relative;
                margin-bottom: 1em;
                padding: 0.5em;
            }

            .month-header {
                position: sticky;
                top: 0;
                background: var(--color-bg);
                font-size: var(--font-size-huge);
                z-index: 1;
                border-radius: calc(2 * var(--border-radius));
                padding: 0.5em;
                color: var(--color-primary);
                font-weight: 300;
            }

            .day {
                position: relative;
            }

            .day-header {
                font-size: var(--font-size-large);
                padding: 0.5em 0.8em;
                /*
                background: var(--color-bg);
                z-index: 1;
                border-radius: calc(2 * var(--border-radius));
                position: sticky;
                top: 2.6em;
                */
            }

            ptc-issue {
                margin: 0 0.5em;
                border-radius: calc(2 * var(--border-radius));
            }

            .placeholder {
                color: var(--color-highlight);
            }

            .placeholder ptc-ui-icon {
                font-size: 5rem;
            }

            .placeholder .msg {
                font-size: var(--font-size-large);
                width: 300px;
                text-align: center;
            }

            .issue-button {
                border: solid 1px;
                margin-left: 0.5em;
                padding: 0.6em;
            }

            .issue-button.ignore {
                color: var(--color-negative);
            }

            .issue-button.solve {
                color: var(--color-primary);
            }

            .issue:not(:hover) .issue-button {
                display: none;
            }
        `,
    ];

    render() {
        const issues = this._issues;

        if (!issues.length) {
            return this._loading
                ? html`
                      <div class="fullbleed center-aligning center-justifying vertical layout scrim">
                          <ptc-ui-spinner active></ptc-ui-spinner>
                      </div>
                  `
                : html`
                      <div class="center-aligning center-justifying fullbleed vertical layout green placeholder">
                          <ptc-ui-icon name="icon.status.all-clear"></ptc-ui-icon>
                          <div class="msg">Es liegen momentan keine Probleme vor!</div>
                      </div>
                  `;
        }

        const months: IssueMonth[] = [];

        let currMonth: IssueMonth | null = null;
        let currDay: IssueDay | null = null;

        const issueCount = new Map<IssueType, number>();
        const filteredTypes = this._filteredTypes;

        for (const issue of issues) {
            const date = parseDateString(issue.date)!;
            if (!currMonth || currMonth.year !== date.getFullYear() || currMonth.month !== date.getMonth()) {
                currMonth = {
                    year: date.getFullYear(),
                    month: date.getMonth(),
                    days: [],
                };
                months.push(currMonth);
            }

            if (!filteredTypes || !filteredTypes.length || filteredTypes.includes(issue.type)) {
                if (!currDay || currDay.date !== issue.date) {
                    currDay = {
                        date: issue.date,
                        issues: [],
                    };
                    currMonth.days.push(currDay);
                }

                currDay.issues.push(issue);
            }

            if (!issueCount.has(issue.type)) {
                issueCount.set(issue.type, 0);
            }

            issueCount.set(issue.type, issueCount.get(issue.type)! + 1);
        }

        return html`
            <div class="sub-menu">
                <form id="filterForm">
                    <div class="padded subtle"><ptc-ui-icon name="icon.action.filter"></ptc-ui-icon> Problemarten</div>
                    ${[...issueCount.entries()].map(
                        ([type, count]) => html`
                            <ptc-checkbox-button
                                buttonClass="small slim transparent"
                                ?hidden=${!count}
                                name="types"
                                .value=${type.toString()}
                                labelPosition="after"
                                .label=${html`
                                    <div class="horizontal spacing center-aligning layout">
                                        <div class="stretch ellipsis collapse">
                                            ${getIssueMessage(type, app.company.country)}
                                        </div>
                                        <div class="smaller red pill">${count}</div>
                                    </div>
                                `}
                                checked
                                @input=${() => this.requestUpdate()}
                            >
                            </ptc-checkbox-button>
                        `
                    )}
                </form>

                <div class="top-margined padded subtle">
                    <ptc-ui-icon name="icon.object.time"></ptc-ui-icon> Zeitraum
                </div>

                <div class="vertical tabs">
                    ${months.map(
                        (m) => html`
                            <button
                                class="center-aligning horizontal layout"
                                ?active=${this._currMonth === `${m.year}-${m.month}`}
                                @click=${() => this._scrollToMonth(m)}
                            >
                                <div class="stretch">${monthNames[m.month]} ${m.year}</div>
                                <div class="tiny red pill">
                                    ${m.days.reduce((total, day) => total + day.issues.length, 0)}
                                </div>
                            </button>
                        `
                    )}
                </div>
            </div>

            <div class="main" @scroll=${this._scroll}>
                ${months.map(({ year, month, days }) => {
                    const issues = days.flatMap((d) => d.issues);
                    return html`
                        <div class="month" data-month="${year}-${month}">
                            <div class="month-header horizontal center-aligning layout">
                                <div class="stretch">${monthNames[month]} ${year}</div>

                                <button
                                    class="small transparent issue-button ignore"
                                    title="Alle Verwerfen"
                                    ?hidden=${!issues.length}
                                    @click=${() => this._ignoreIssues(issues)}
                                >
                                    <ptc-ui-icon name="icon.toggle.visibility-hidden"></ptc-ui-icon>
                                    Alle Verwerfen
                                </button>
                            </div>

                            ${days.map(({ date, issues }) => {
                                const d = parseDateString(date)!;
                                return html`
                                    <div class="day">
                                        <div class="day-header">${weekDays[d.getDay()]}, ${formatDate(d)}</div>

                                        ${issues.map((issue) => this._renderIssue(issue))}
                                    </div>
                                `;
                            })}
                        </div>
                    `;
                })}
            </div>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }

    private _renderIssue(issue: Issue) {
        return html`
            <div class="horizontal spacing center-aligning layout issue">
                <ptc-issue .issue=${issue}></ptc-issue>

                <div class="stretch"></div>

                <button
                    class="transparent small issue-button solve"
                    @click=${() => this._goToIssue(issue)}
                    ?hidden=${!this._canResolve(issue)}
                >
                    <ptc-ui-icon name="icon.action.resolve"></ptc-ui-icon>
                    Beheben
                </button>

                ${issue.type !== IssueType.UnbalancedDailyCash && issue.type !== IssueType.PendingDailyRevenues
                    ? html`
                          <button
                              class="transparent small issue-button ignore"
                              @click=${() => this._ignoreIssues([issue])}
                          >
                              <ptc-ui-icon name="icon.toggle.visibility-hidden"></ptc-ui-icon>
                              Verwerfen
                          </button>
                      `
                    : ""}
            </div>
        `;
    }
}
